/**
 * PCB Rules action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import type {PcbRulesMap, PcbRuleSetsMap} from "@buildwithflux/core";
import type {IPcbLayoutNodesRuleSetsMap, RuleKey} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

/**
 * Hook to get the setGlobalPcbRuleSet action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 *
 * @returns A function to set global PCB rule sets
 */
export function useSetGlobalPcbRuleSet(): (
    setPcbLayoutRules: PcbRuleSetsMap,
    selectedGlobalPcbLayoutRule?: string,
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (setPcbLayoutRules: PcbRuleSetsMap, selectedGlobalPcbLayoutRule?: string) => {
            documentStore.getState().setGlobalPcbRuleSet(setPcbLayoutRules, selectedGlobalPcbLayoutRule);
        },
        [documentStore],
    );
}

/**
 * Hook to get the removeGlobalPcbRuleSet action
 *
 * Calls direct Zustand store method.
 *
 * @returns A function to remove global PCB rule sets by their UIDs
 */
export function useRemoveGlobalPcbRuleSet(): (pcbLayoutRuleUids: string[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (pcbLayoutRuleUids: string[]) => {
            documentStore.getState().removeGlobalPcbRuleSet(pcbLayoutRuleUids);
        },
        [documentStore],
    );
}

/**
 * Hook to get the toggleGlobalPcbRule action
 *
 * Calls direct Zustand store method.
 *
 * @param ruleId - The ID of the rule to toggle
 * @param state - Whether the rule should be enabled or disabled
 * @param subRuleId - Optional sub-rule ID
 * @returns A function to toggle a global PCB rule
 */
export function useToggleGlobalPcbRule(): (ruleId: string, state: boolean, subRuleId?: string) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (ruleId: string, state: boolean, subRuleId?: string) => {
            documentStore.getState().toggleGlobalPcbRule(ruleId, state, subRuleId);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setNodeRulesMulti action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 *
 * @returns A function to set rules on multiple PCB nodes
 */
export function useSetNodeRulesMulti(): (nodeUidRulesMap: {[nodeUid: string]: PcbRulesMap}) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (nodeUidRulesMap: {[nodeUid: string]: PcbRulesMap}) => {
            documentStore.getState().setNodeRulesMulti(nodeUidRulesMap);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setNodeRules action (from @buildwithflux/core)
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 *
 * @returns A function to set rules on PCB nodes
 */
export function useSetNodeRules(): (updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap) => {
            documentStore.getState().setNodeRules(updatedNodesRulesSetsMap);
        },
        [documentStore],
    );
}

/**
 * Hook to get the duplicateGlobalPcbRuleSet action
 *
 * Calls direct Zustand store method.
 *
 * @returns A function to duplicate global PCB rule sets by their UIDs
 */
export function useDuplicateGlobalPcbRuleSet(): (ruleUids: string[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (ruleUids: string[]) => {
            documentStore.getState().duplicateGlobalPcbRuleSet(ruleUids);
        },
        [documentStore],
    );
}

/**
 * Hook to get the removeNodeRules action
 *
 * Calls direct Zustand store method.
 *
 * @returns A function to remove rules from a specific PCB node
 */
export function useRemoveNodeRules(): (nodeId: string, ruleKeys: RuleKey[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (nodeId: string, ruleKeys: RuleKey[]) => {
            documentStore.getState().removeNodeRules(nodeId, ruleKeys);
        },
        [documentStore],
    );
}

/**
 * Helper function to dispatch toggleNodeSpecificRule action
 *
 * This helper encapsulates the context gathering (document state from Zustand)
 * and dispatch logic, making it reusable in both React hooks and non-React contexts.
 *
 * @param ruleId - The ID of the rule to toggle
 * @param nodeId - The ID of the node containing the rule
 * @param toggleState - Whether the rule should be enabled or disabled
 * @param documentStore - The DocumentStore from FluxServices
 */
export function dispatchToggleNodeSpecificRule(
    ruleId: string,
    nodeId: string,
    toggleState: boolean,
    documentStore: DocumentStore,
): void {
    documentStore.getState().toggleNodeSpecificRule(ruleId, nodeId, toggleState);
}

/**
 * Hook to get the toggleNodeSpecificRule action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 * Gets document state from Zustand store instead of Redux.
 *
 * @returns A function to toggle a node-specific PCB rule
 */
export function useToggleNodeSpecificRule(): (ruleId: string, nodeId: string, toggleState: boolean) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (ruleId: string, nodeId: string, toggleState: boolean) => {
            dispatchToggleNodeSpecificRule(ruleId, nodeId, toggleState, documentStore);
        },
        [documentStore],
    );
}

/**
 * Factory function to create PCB rules actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch PCB rule actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with PCB rules action methods
 */
export function createPcbRulesActions(documentStore: DocumentStore) {
    return {
        setGlobalPcbRuleSet: (setPcbLayoutRules: PcbRuleSetsMap, selectedGlobalPcbLayoutRule?: string) => {
            documentStore.getState().setGlobalPcbRuleSet(setPcbLayoutRules, selectedGlobalPcbLayoutRule);
        },
        removeGlobalPcbRuleSet: (pcbLayoutRuleUids: string[]) => {
            documentStore.getState().removeGlobalPcbRuleSet(pcbLayoutRuleUids);
        },
        toggleGlobalPcbRule: (ruleId: string, state: boolean, subRuleId?: string) => {
            documentStore.getState().toggleGlobalPcbRule(ruleId, state, subRuleId);
        },
        setNodeRulesMulti: (nodeUidRulesMap: {[nodeUid: string]: PcbRulesMap}) => {
            documentStore.getState().setNodeRulesMulti(nodeUidRulesMap);
        },
        setNodeRules: (updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap) => {
            documentStore.getState().setNodeRules(updatedNodesRulesSetsMap);
        },
        duplicateGlobalPcbRuleSet: (ruleUids: string[]) => {
            documentStore.getState().duplicateGlobalPcbRuleSet(ruleUids);
        },
        removeNodeRules: (nodeId: string, ruleKeys: RuleKey[]) => {
            documentStore.getState().removeNodeRules(nodeId, ruleKeys);
        },
        toggleNodeSpecificRule: (ruleId: string, nodeId: string, toggleState: boolean) => {
            documentStore.getState().toggleNodeSpecificRule(ruleId, nodeId, toggleState);
        },
    };
}
